import { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import { tooltipStyle, COLORS } from '../../utils/colors';

/**
 * Grouped horizontal bar chart for compare mode.
 * Groups = question options (y axis); one bar per segment within each group.
 *
 * Props:
 *   options — group labels (question options)
 *   series  — [{ name: segment, color, data: [value per option] }]
 *   pct     — append % to ticks/labels
 *   xMax    — x axis max (default 100)
 */
export default function CompareBarChart({ options, series, pct = false, xMax = 100 }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !options || !series) return;

    if (chartRef.current) chartRef.current.destroy();

    const labels = options.map(o => truncateLabel(o, 40));
    const fullLabels = options;

    const datasets = series.map(s => ({
      label: s.name,
      data: s.data,
      backgroundColor: s.color,
      borderWidth: 0,
      borderRadius: 4,
      barPercentage: 0.85,
      categoryPercentage: 0.78
    }));

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            beginAtZero: true,
            max: xMax,
            grid: { color: '#f0f1f3' },
            ticks: {
              font: { size: 11 },
              color: COLORS.textMuted,
              callback: (v) => v + (pct ? '%' : '')
            }
          },
          y: {
            grid: { display: false },
            ticks: {
              font: { size: 12, weight: 500 },
              color: COLORS.textSecondary,
              padding: 8
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              title: (items) => fullLabels[items[0].dataIndex],
              label: (item) => ` ${item.dataset.label}: ${item.raw != null ? item.raw.toFixed(1) : '—'}${pct ? '%' : ''}`
            }
          },
          datalabels: {
            display: true,
            anchor: 'end',
            align: 'right',
            color: COLORS.textSecondary,
            font: { size: 10, weight: 600 },
            formatter: (v) => (v != null ? v.toFixed(1) + (pct ? '%' : '') : '')
          }
        }
      }
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [options, series, pct, xMax]);

  return <canvas ref={canvasRef} />;
}

function truncateLabel(text, maxLen) {
  if (!text) return '';
  return text.length > maxLen ? text.slice(0, maxLen - 3) + '...' : text;
}
